import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';
import { CONFIG_ITEM_KEYS } from '../../../ui/prebuilt/config-items/config-item-keys';
import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { addAnalytics } from '../../../ui/utils/analytics/utils';

export const firePromptConfigNotFoundError = ({
	editorView,
	dispatch,
	prompt,
	adjustSelection,
	promptText,
}: {
	adjustSelection?: string;
	dispatch: EditorView['dispatch'];
	editorView: EditorView;
	prompt?: string;
	promptText?: string;
}): void => {
	const errorEventTr = addAnalytics({
		editorState: editorView.state,
		tr: editorView.state.tr,
		payload: {
			action: 'error',
			actionSubject: 'editorPluginAI',
			actionSubjectId: 'eventHubPlugin',
			attributes: {
				errorType: 'promptConfigItemNotFound',
				prompt: prompt,
				promptText: promptText,
				adjustSelection: adjustSelection,
			},
			eventType: EVENT_TYPE.OPERATIONAL,
		},
	});
	dispatch(errorEventTr);
};

type SuggestedTitlePayload = {
	action: 'streamingCompleted';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'eventHubPlugin';
	attributes: {
		[key: string]: unknown;
		analyticSourceId: string;
		fallbackSuggestedTitleReturned?: boolean;
		isDateTimeFormat?: boolean;
		modalHidden: boolean;
		promptType: string;
		suggestedTitleFingerprint?: string;
	};
	eventType: EVENT_TYPE.TRACK;
};

function fireSuggestedTitleAnalyticsEvent({
	api,
	analyticSourceId,
	additionalAttributes,
}: {
	additionalAttributes?: Record<string, unknown>;
	analyticSourceId: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}) {
	api?.analytics?.actions.fireAnalyticsEvent<SuggestedTitlePayload, 'customEventType'>({
		action: 'streamingCompleted',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'eventHubPlugin',
		attributes: {
			promptType: CONFIG_ITEM_KEYS.SUGGEST_A_TITLE,
			modalHidden: true,
			analyticSourceId,
			...additionalAttributes,
		},
		eventType: EVENT_TYPE.TRACK,
	});
}

export const fireFallbackSuggestedTitleReturnedEvent = ({
	api,
	analyticSourceId,
	isDateTimeFormat,
}: {
	analyticSourceId: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	isDateTimeFormat?: boolean;
}): void => {
	fireSuggestedTitleAnalyticsEvent({
		api,
		analyticSourceId,
		additionalAttributes: {
			fallbackSuggestedTitleReturned: true,
			isDateTimeFormat,
		},
	});
};

async function hashString(input: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(input);
	const hashBuffer = await crypto.subtle.digest('SHA-256', data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
	return hashHex;
}

export const isSuggestTitle = (configItem: EditorPluginAIConfigItem): boolean =>
	configItem.key === CONFIG_ITEM_KEYS.SUGGEST_A_TITLE;

export const fireSuggestedTitleCompletedEvent = async ({
	api,
	analyticSourceId,
	suggestedTitle,
}: {
	analyticSourceId: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	suggestedTitle: string;
}): Promise<void> => {
	const suggestedTitleFingerprint = await hashString(suggestedTitle);
	fireSuggestedTitleAnalyticsEvent({
		api,
		analyticSourceId,
		additionalAttributes: {
			fallbackSuggestedTitleReturned: false,
			suggestedTitleFingerprint,
		},
	});
};
